/*
 * behaviors index.js
 */
const _require = require('@truqc/content-path'),
  appendPath = _require.appendPath,
  setValue = _require.setValue,
  getValue = _require.getValue,
  removeValue = _require.removeValue;
const _require2 = require('semver/preload'),
  gte = _require2.gte,
  coerce = _require2.coerce,
  gt = _require2.gt;
const _require3 = require('./lib/mergePatch'),
  generateMergePatch = _require3.generateMergePatch,
  applyMergePatch = _require3.applyMergePatch;
const _require4 = require('./behaviors/formulaBehavior'),
  calculate = _require4.calculate;
const _require5 = require('./lib/evaluateFormat'),
  evaluateFormat = _require5.evaluateFormat;
const migrationVersions = require('./lib/migrationVersions');
const _require6 = require('./lib/profile'),
  clearResults = _require6.clearResults,
  printResults = _require6.printResults,
  profile = _require6.profile;
const _require7 = require('./lib/permissions'),
  checkPermission = _require7.checkPermission;
const _require8 = require('./lib/atomic'),
  createAtomicPatch = _require8.createAtomicPatch;
const _require9 = require('./lib/formatPaths'),
  keyPathsToCriteria = _require9.keyPathsToCriteria;
const _require0 = require('./lib/evaluateSource'),
  evaluateSource = _require0.evaluateSource,
  evaluateSources = _require0.evaluateSources,
  handleError = _require0.handleError,
  clearBehaviorCache = _require0.clearBehaviorCache;
const clients = {
  ios: 'ios',
  server: 'server',
  web: 'web'
};
const parseEvent = eventIn => {
  const length = eventIn.length;
  if (eventIn.endsWith("/".concat(clients.ios))) {
    return {
      event: eventIn.substring(0, length - clients.ios.length - 1),
      client: clients.ios
    };
  }
  if (eventIn.endsWith("/".concat(clients.server))) {
    return {
      event: eventIn.substring(0, length - clients.server.length - 1),
      client: clients.server
    };
  }
  if (eventIn.endsWith("/".concat(clients.web))) {
    return {
      event: eventIn.substring(0, length - clients.web.length - 1),
      client: clients.web
    };
  }
  return {
    event: eventIn
  };
};
const includesEvent = (currentEvent, currentClient, behaviorEvents) => behaviorEvents.some(behaviorEvent => {
  const _parseEvent = parseEvent(behaviorEvent),
    event = _parseEvent.event,
    client = _parseEvent.client;
  if ((client === undefined || client === currentClient) && event === currentEvent) {
    return true;
  }
  return false;
});

/**
  * @function extractBehaviors - extract behaviors for a specific event from a component.
  */

const _extractBehaviors = function extractBehaviors(event, component, content, client) {
  let componentParentPath = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : '';
  let behaviors = [];
  const processBehavior = (events, source, parentPath, targetPath) => {
    if (includesEvent(event, client, events)) {
      const behavior = {};
      if (source !== undefined) {
        behavior.source = source;
      }
      if (parentPath !== undefined) {
        behavior.parentPath = parentPath;
      }
      if (targetPath !== undefined) {
        behavior.targetPath = targetPath;
      }
      behaviors.push(behavior);
    }
  };
  if (component.source !== undefined && component.contentPath !== undefined) {
    processBehavior(['update', 'save'], component.source, componentParentPath, component.contentPath);
  }
  const componentPath = component.contentPath ? appendPath(componentParentPath, component.contentPath) : componentParentPath;
  if (component.behaviors !== undefined) {
    component.behaviors.forEach(behavior => {
      processBehavior(Array.isArray(behavior.event) ? behavior.event : [behavior.event], behavior.source, componentPath, behavior.targetContentPath);
    });
  }
  if (component.children !== undefined) {
    const processChildren = contentPath => {
      component.children.forEach(childComponent => {
        behaviors = behaviors.concat(_extractBehaviors(event, childComponent, content, client, contentPath));
      });
    };
    if (component.type === 'collection') {
      const childrenContent = getValue(componentPath, content);
      if (childrenContent !== undefined && childrenContent !== null) {
        const ids = Object.keys(childrenContent);
        ids.forEach(id => {
          const itemPath = appendPath(componentPath, id);
          processChildren(itemPath);
        });
      }
    } else {
      processChildren(componentPath);
    }
  }
  return behaviors;
};

/**
  * @function evaluateEvent - evaluate behaviors for a specific event. returns a merge patch
  */

const evaluateEvent = async (event, contentIn, format, path, nativeFunctions, clientIn) => {
  const evaluateEventProfile = profile('evaluateEvent');
  const behaviors = _extractBehaviors(event, format, contentIn, clientIn, path);
  if (behaviors.length === 0) {
    evaluateEventProfile.end();
    // early return avoiding copy of content
    return {};
  }
  let content = contentIn;
  if (behaviors.some(item => item.targetPath !== undefined)) {
    // only copy content if writing
    content = JSON.parse(JSON.stringify(contentIn));
  }
  for (let i = 0; i < behaviors.length; i += 1) {
    const behavior = behaviors[i];
    /* eslint-disable-next-line no-await-in-loop */
    const value = await evaluateSource(behavior.source, content, behavior.parentPath, nativeFunctions.transientContext || {}, nativeFunctions);
    if (behavior.targetPath !== undefined) {
      const behaviorPath = appendPath(behavior.parentPath, behavior.targetPath);
      if (value === undefined || value === null) {
        content = removeValue(behaviorPath, content);
      } else if (behaviorPath === '/') {
        content = value;
      } else {
        content = setValue(behaviorPath, content, value);
      }
    }
  }
  const result = generateMergePatch(contentIn, content);
  evaluateEventProfile.end();
  return result !== undefined ? result : {};
};
const evaluate = (event, contentIn, format, nativeFunctions, clientIn) => evaluateEvent(event, contentIn, format, '', nativeFunctions, clientIn);

/**
 * performs evaluate of all migration behaviors in a format given content.
 * The general algorithm is thus:  look at the version in the content.  That defines
 * the last version that this content used for formatting.  Look at the new version of
 * format in the report.  If they are not the same, then we need to look for all
 * events that start with `migrateVersion_`.  the next values will be the version
 * that that migration should be executed on, e.g. `migrateVersion_3.2.0` should be
 * executed if the content version is 3.2.0 or less, and the (new) format version is
 * higher than 3.2.0.
 *
 * once it finds all those migration behaviors, it creates a merge patch from them,
 * included a patch to move the content version to the new value from the format so
 * that the migration doesn't happen twice.
 */
const migrateContent = async function migrateContent(format, content, nativeFunctions) {
  let clientIn = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : '';
  // need to rewrite report type version, even if there are no migrations so
  // that the content knows it is up to date with the lates version.
  let resultPatch = {
    version: format.version
  };
  let versions = migrationVersions.getMigrationVersions(format);

  // trim all versions less than the content version
  versions = versions.filter(event => gte(event, content.version));
  if (versions.length > 0) {
    let result = JSON.parse(JSON.stringify(content));

    // now, run through these versions as custom events
    for (let i = 0; i < versions.length; i += 1) {
      const event = "migrateVersion_".concat(versions[i]);

      // eslint-disable-next-line no-await-in-loop
      const mergePatch = await evaluate(event, result, format, nativeFunctions, clientIn);
      result = applyMergePatch(result, mergePatch);

      // reset version on mergePatch
      result.version = format.version;
    }
    resultPatch = generateMergePatch(content, result);
  }
  return resultPatch;
};

/**
 * checks to see if there is a version mismatch in content versus format
 * and runs through migration events to see if there are migrations to
 * execute.
 *
 * returns undefined if there is no content migration.
 */
const checkAndMigrate = async (formatInput, content, nativeFunctions) => {
  let result;
  const formatVersion = coerce(formatInput.version);
  // grab the version - wish these were all the same!
  // the server code removes reportTypeVersion, issueTypeVersion, and formTypeVersion
  // so once every content item has been migrated, we can remove this line.
  let contentVersion = content.version || content.reportTypeVersion || content.issueTypeVersion || content.formTypeVersion;
  if (typeof contentVersion !== 'string') {
    // need to assign content version.  must be a create event.
    return {
      version: formatInput.version
    };
  }
  contentVersion = coerce(contentVersion);
  if (gt(formatVersion, contentVersion)) {
    // run content migration, which returns a mergePatch.
    result = await migrateContent(formatInput, content, nativeFunctions, 'server');
  }
  return result;
};

/*
 * Functions exposed for iPad client interaction.
 * All of these functions server two purposes:
 * 1. Converting between async/await and callback (JS bridge only supports latter).
 * 2. Serialising and deserialising to JSON for crossing the bridge
 *    (otherwise each individual item is bridged separately, which is slooooow).
 */

const beginProfilingWrapper = () => {
  clearResults();
};
const calculateWrapper = (formula, variablesJSON) => calculate(formula, JSON.parse(variablesJSON));
const checkAndMigrateCallbackWrapper = async (contentJSON, formatJSON, nativeFunctions, callbackFunc) => {
  let result = {};
  try {
    const content = JSON.parse(contentJSON);
    const format = JSON.parse(formatJSON);
    result = await checkAndMigrate(format, content, nativeFunctions);
  } catch (error) {
    handleError(error, nativeFunctions);
  }
  callbackFunc(JSON.stringify(result));
};
const checkPermissionWrapper = (resourceJSON, action, permissions, user) => {
  const resource = JSON.parse(resourceJSON);
  return checkPermission(resource, action, permissions, user);
};
const endProfilingWrapper = () => {
  printResults(true, 10);
};
const evaluateEventCallbackWrapper = async (event, contentJSON, formatJSON, path, nativeFunctions, callbackFunc) => {
  let result = {};
  try {
    result = await evaluateEvent(event, JSON.parse(contentJSON), JSON.parse(formatJSON), path, nativeFunctions, 'ios');
  } catch (error) {
    handleError(error, nativeFunctions);
  }
  callbackFunc(JSON.stringify(result));
};
const evaluateFormatCallbackWrapper = async (contentJSON, formatJSON, path, nativeFunctions, callbackFunc) => {
  let result = {};
  try {
    const content = JSON.parse(contentJSON);
    const format = JSON.parse(formatJSON);

    // Remove `children` from `excludePropertyMap`
    // for https://app.clickup.com/t/86dwz58yv
    // which has only ever been done on iPad anyway.
    const excludePropertyMap = {
      //   // defer evaluation of buttons until tapped
      //   modalButton: ['children'],
      //   operationButton: ['children', 'options'],
      operationButton: ['options']
    };
    result = await evaluateFormat(content, format, path, nativeFunctions, excludePropertyMap);
  } catch (error) {
    handleError(error, nativeFunctions);
  }
  callbackFunc(JSON.stringify(result));
};
const evaluateSourceCallbackWrapper = async (sourceJSON, contentJSON, path, transientContextJSON, nativeFunctions, callbackFunc) => {
  let result;
  try {
    result = await evaluateSource(JSON.parse(sourceJSON), JSON.parse(contentJSON), path, JSON.parse(transientContextJSON), nativeFunctions);
  } catch (error) {
    handleError(error, nativeFunctions);
  }
  callbackFunc(JSON.stringify(result));
};
const evaluateSourcesCallbackWrapper = async (sourcesJSON, contentJSON, path, transientContextJSON, nativeFunctions, callbackFunc) => {
  let result;
  try {
    result = await evaluateSources(JSON.parse(sourcesJSON), JSON.parse(contentJSON), path, JSON.parse(transientContextJSON), nativeFunctions);
  } catch (error) {
    handleError(error, nativeFunctions);
  }
  callbackFunc(JSON.stringify(result));
};
const createAtomicPatchWrapper = (formatInputJSON, mergePatchJSON) => {
  const formatInput = JSON.parse(formatInputJSON);
  const mergePatch = JSON.parse(mergePatchJSON);
  return createAtomicPatch(formatInput, mergePatch);
};
const keyPathsToCriteriaWrapper = (criteriaJSON, formatInputJSON) => {
  const criteriaIn = JSON.parse(criteriaJSON);
  const formatInput = JSON.parse(formatInputJSON);
  return keyPathsToCriteria(criteriaIn, formatInput);
};
module.exports = {
  checkAndMigrate,
  clearBehaviorCache,
  evaluate,
  evaluateFormat,
  migrateContent,
  // Functions exposed for iPad client interaction
  beginProfilingWrapper,
  calculateWrapper,
  checkAndMigrateCallbackWrapper,
  checkPermissionWrapper,
  endProfilingWrapper,
  evaluateEventCallbackWrapper,
  evaluateFormatCallbackWrapper,
  evaluateSourceCallbackWrapper,
  evaluateSourcesCallbackWrapper,
  createAtomicPatchWrapper,
  keyPathsToCriteriaWrapper
};